import * as React from 'react';
import { LocalizeContextProps, withLocalize } from 'react-localize-redux';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import Select, { ValueType } from 'react-select';
import { Button, Form, Input } from 'reactstrap';
import { Dispatch } from 'redux';
import { Group, Privilege } from '../../clients/aasServerClient.generated';
import { clearSingleGroup, fetchSingleGroup, saveSingleGroup } from '../../stores/groups/actionCreators';
import { getSingleGroup } from '../../stores/groups/selectors';
import { isLoading } from '../../stores/navigation/selectors';
import { RootState } from '../../stores/store';
import { workspaceOptions } from '../../stores/workspaces/selectors';
import SideModal from '../common/modals/SideModal';
import { LoadingButton } from '../navigation/LoadingButton';
import LoadingScreen from '../navigation/LoadingScreen';
import ServerSelect from '../servers/ServerSelect';
import WorkspaceSelect from '../workspaces/WorkspaceSelect';
import GroupProperties from './GroupProperties';

interface Props extends LocalizeContextProps, RouteComponentProps<{ id: string }> {
    isLoading: boolean;
    group: {
        loaded: boolean;
        saved: boolean;
        group?: Partial<Group>;
    };
    workspaces: Array<{ label: string, value: string }>;

    fetchSingleGroup: (id: string) => void;
    clearSingleGroup: () => void;
    saveSingleGroup: (
        group: Partial<Group>,
        workspaces?: Array<{ id: string, value: boolean }>,
    ) => void;
}

interface State {
    group?: Partial<Group>;
    workspaceSelection?: ValueType<{ label: string, value: string }>;
}

class EditGroupPage extends React.Component<Props, State> {

    public constructor(props: Props) {
        super(props);
        this.state = {
            group: undefined,
        };
        const id = this.props.match.params.id;
        this.props.clearSingleGroup();
        this.props.fetchSingleGroup(id);
    }

    public componentDidUpdate(prevProps: Props) {
        if (!prevProps.group.loaded && this.props.group.loaded) {
            this.setState({
                group: this.props.group.group,
            });
        }
        if (!prevProps.group.saved && this.props.group.saved) {
            this.closePage();
        }
    }
    public modalRef = React.createRef<HTMLDivElement>();
    public render() {
        const t = this.props.translate;
        return (
            <SideModal
                isOpen={true}
                toggle={this.closePage}
                headerText={t('groups.edit.header.edit') as string}
                footer={this.renderFooter()}
                objectRef={this.modalRef}
            >
                {this.props.group.loaded && this.props.group.group ? (
                    <Form>
                        <h3 className="mb-4 mt-2">{t('groups.edit.section.details')}</h3>
                        <GroupProperties
                            type="edit"
                            group={this.props.group.group}
                            updateGroup={this.updateGroup}
                        />
                        {this.props.group.group!.workspaces ? (
                            <React.Fragment>
                                <hr className="fill-modal" />
                                <h3 className="mb-4 mt-4">{t('groups.edit.section.workspaces')}</h3>
                                <WorkspaceSelect
                                    isMulti={true}
                                    selected={this.props.group.group.workspaces.map((w) => w.id)}
                                    updateSelection={this.updateWorkspaceSelection}
                                    selection={this.state.workspaceSelection}
                                />
                            </React.Fragment>
                        ) : null}
                    </Form>
                ) : (
                        <LoadingScreen text={t('loading.loading') as string} />
                    )}
            </SideModal>
        );
    }

    private updateGroup = (group: Partial<Group>) => {
        this.setState({
            group,
        });
    }

    private updateWorkspaceSelection = (selection: ValueType<{ label: string, value: string }>) => {
        this.setState({ workspaceSelection: selection });
    }

    private closePage = () => {
        this.props.history.push('/groups');
    }

    private renderFooter = (): React.ReactNode => {
        const t = this.props.translate;
        return (
            <React.Fragment>
                <LoadingButton
                    loading={this.props.isLoading}
                    color="success"
                    onClick={this.saveGroup}
                    style={{ width: '200px' }}
                >
                    {t('groups.edit.button.save')}
                </LoadingButton>
            </React.Fragment>
        );
    }

    private saveGroup = () => {
        const group = this.state.group;
        let workspaces: Array<{ id: string; value: boolean }> | undefined;
        if (this.state.workspaceSelection) {
            workspaces = this.props.workspaces.map((w) => ({
                id: w.value,
                // tslint:disable-next-line:no-any
                value: (this.state.workspaceSelection as any[]).find((x) => x.value === w.value) !== undefined,
            }));
        }

        this.props.saveSingleGroup(group!, workspaces);
    }
}

const mapStateToProps = (state: RootState) => ({
    group: getSingleGroup(state),
    isLoading: isLoading(state),
    workspaces: workspaceOptions(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    clearSingleGroup: () => dispatch(clearSingleGroup()),
    fetchSingleGroup: (id: string) => dispatch(fetchSingleGroup(id)),
    saveSingleGroup: (
        group: Partial<Group>,
        workspaces?: Array<{ id: string, value: boolean }>,
    ) => dispatch(saveSingleGroup(group, workspaces))
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(withLocalize(EditGroupPage)));